// Supplies the auth-page footers with the operating organization's details.
//
// One hook shared by all three auth screens (login, signup, password-reset) because the footer is inlined
// per page rather than living in a shared component — so without a shared hook each page would fetch and
// interpret the response its own way, which is the same duplication that let the three footers drift from
// the rest of the product in the first place (SHCT-624).

import { useEffect, useState } from 'react';
import { fetchPublicInstitution, type PublicInstitution } from '@/api/institution';

const EMPTY: PublicInstitution = { name: null, address: null, representative: null };

/**
 * Reads the organization details for the footer.
 *
 * Renders nothing rather than something wrong. While the request is in flight — and if it fails — every
 * field stays null and the footer omits those rows, filling them in once the data arrives. There is
 * deliberately no hardcoded fallback text: a fallback is exactly the stale copy this work removes, and it
 * would silently restore the defect the moment the endpoint broke.
 *
 * A failure is logged rather than surfaced as a toast or modal. This is a knowing departure from the
 * repository's error-handling standard, which classifies an API failure as a System Error that must be
 * shown to the user. The footer is decorative page furniture on a screen whose actual purpose — logging
 * in — is unaffected by its absence, so an error dialog here would interrupt a working login to report
 * that a line of small print is missing. Recorded as a tracked deviation rather than made quietly.
 */
export function useInstitution(): PublicInstitution {
  const [institution, setInstitution] = useState<PublicInstitution>(EMPTY);

  useEffect(() => {
    let active = true;
    fetchPublicInstitution()
      .then((data) => {
        // Guard against a response arriving after the screen has gone: setting state on an unmounted
        // component is a React warning, and on these screens a successful login unmounts the page.
        if (active) setInstitution(data);
      })
      .catch((e: unknown) => {
        console.error('[institution] footer organization details unavailable', e);
      });
    return () => {
      active = false;
    };
  }, []);

  return institution;
}
